import React from 'react';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './ClassifiedsPage.module.css';

const MODEL_LINKS = [
  { href: '/cars/porsche-993', label: 'Porsche 993' },
  { href: '/cars/porsche-964', label: 'Porsche 964' },
  { href: '/cars/porsche-911-gt3', label: 'Porsche 911 GT3' },
  { href: '/cars/porsche-997', label: 'Porsche 997' },
  { href: '/cars/porsche-996', label: 'Porsche 996' },
  { href: '/cars/porsche-930', label: 'Porsche 930 Turbo' },
  { href: '/cars/porsche-cayman', label: 'Porsche Cayman' },
  { href: '/cars/porsche-boxster', label: 'Porsche Boxster' },
  { href: '/cars/porsche-944', label: 'Porsche 944' },
  { href: '/cars/porsche-356', label: 'Porsche 356' },
];

const FAQ_ITEMS = [
  {
    q: 'What is Rennzone?',
    a: "Rennzone is the world's only Porsche-exclusive marketplace — a dedicated platform for buying and selling Porsche cars, parts, books, memorabilia, and race cars. Every listing is Porsche. Every buyer is an enthusiast.",
  },
  {
    q: 'Is Rennzone free to browse?',
    a: 'Yes. Browsing all Porsche classifieds on Rennzone is completely free. You only need an account to contact a seller or create a listing.',
  },
  {
    q: 'Does Rennzone cover Porsche parts and memorabilia as well as cars?',
    a: 'Yes — Rennzone lists Porsche road cars, race cars, parts, books, factory literature, memorabilia, and collectibles. All in one place, all Porsche.',
  },
  {
    q: 'Is Rennzone global or UK-only?',
    a: 'Rennzone is a global marketplace with sellers and buyers across Europe, North America, the Middle East, and Asia-Pacific.',
  },
];

const ClassifiedsPage = props => {
  const { scrollingDisabled } = props;

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <Page
      title="Porsche Classifieds — Buy & Sell Porsche Cars, Parts & Memorabilia"
      description="The Porsche classifieds marketplace. Buy and sell Porsche cars, parts, books and memorabilia globally. Only Porsche. Created by enthusiasts, for enthusiasts."
      schema={faqSchema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mainColumnClassName={css.main}
      >
        <div className={css.root}>

          {/* Breadcrumb */}
          <div className={css.breadcrumb}>
            <div className={css.breadcrumbInner}>
              <a href="/" className={css.breadcrumbLink}>Home</a>
              <span className={css.breadcrumbSep}>›</span>
              <span className={css.breadcrumbCurrent}>Porsche Classifieds</span>
            </div>
          </div>

          {/* Hero */}
          <div className={css.hero}>
            <div className={css.heroInner}>
              <span className={css.heroLabel}>Porsche Marketplace</span>
              <h1 className={css.h1}>Porsche Classifieds</h1>
              <p className={css.heroSubtitle}>
                The only marketplace built exclusively for the Porsche community.
                Cars, parts, race cars, books, and memorabilia — all Porsche, all the time.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className={css.statsRow}>
            <div className={css.statsInner}>
              <div className={css.stat}>
                <span className={css.statValue}>100%</span>
                <span className={css.statLabel}>Porsche only</span>
              </div>
              <div className={css.stat}>
                <span className={css.statValue}>Global</span>
                <span className={css.statLabel}>Buyers & sellers worldwide</span>
              </div>
              <div className={css.stat}>
                <span className={css.statValue}>5 categories</span>
                <span className={css.statLabel}>Cars, Parts, Race Cars, Books, Memorabilia</span>
              </div>
              <div className={css.stat}>
                <span className={css.statValue}>Free</span>
                <span className={css.statLabel}>To browse all listings</span>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className={css.body}>
            <div className={css.bodyInner}>

              {/* Left — content */}
              <div className={css.content}>

                <h2 className={css.h2}>What's Listed on Rennzone</h2>
                <p className={css.p}>
                  Rennzone covers the full spectrum of Porsche ownership:
                </p>
                <ul className={css.list}>
                  <li><strong>Road cars</strong> — from classic 356s to modern 992 GT3s, every generation and variant</li>
                  <li><strong>Race cars</strong> — Cup cars, GT3 RS, historic competition Porsches, and motorsport builds</li>
                  <li><strong>Parts</strong> — OEM, genuine, performance, and restoration parts for all models</li>
                  <li><strong>Books and literature</strong> — factory manuals, press kits, photography books, and brochures</li>
                  <li><strong>Memorabilia</strong> — signed helmets, factory watches, die-cast models, and period advertising</li>
                </ul>

                <h2 className={css.h2}>Why Porsche-Only Matters</h2>
                <p className={css.p}>
                  General classifieds platforms list everything. Rennzone lists only Porsche.
                  Sellers on Rennzone understand exactly what they are selling — model variants,
                  matching numbers, service history requirements, and the value of rare options.
                  Buyers arrive already knowledgeable, which means faster sales and better-informed
                  transactions at every price point.
                </p>
                <p className={css.p}>
                  A 993 RS on a generalist platform sits next to used furniture. On Rennzone, it
                  sits next to serious Porsche listings, seen exclusively by buyers who understand
                  what a 993 RS is worth.
                </p>

                <h2 className={css.h2}>Global Reach</h2>
                <p className={css.p}>
                  Rennzone connects buyers and sellers across Europe, North America, the Middle
                  East, and Asia-Pacific. Rare Porsche variants in one market often command
                  premiums in another — global reach means global price discovery and faster sales
                  for sellers of specialist models.
                </p>

                <h2 className={css.h2}>Browse by Model</h2>
                <div className={css.linkGrid}>
                  {MODEL_LINKS.map(({ href, label }) => (
                    <a key={href} href={href} className={css.linkCard}>{label} →</a>
                  ))}
                </div>

                <h2 className={css.h2}>Browse by Category</h2>
                <div className={css.linkGrid}>
                  <a href="/cars/race-cars" className={css.linkCard}>Race Cars →</a>
                  <a href="/cars/air-cooled" className={css.linkCard}>Air-Cooled →</a>
                  <a href="/parts" className={css.linkCard}>Parts →</a>
                  <a href="/memorabilia" className={css.linkCard}>Memorabilia & Books →</a>
                </div>

                <div className={css.ctaRow}>
                  <a href="/s" className={css.ctaPrimary}>Browse All Listings</a>
                  <a href="/l/new" className={css.ctaSecondary}>List Your Porsche</a>
                </div>

                <h2 className={css.h2}>Frequently Asked Questions</h2>
                <dl className={css.faqList}>
                  {FAQ_ITEMS.map((item, i) => (
                    <div key={i} className={css.faqItem}>
                      <dt className={css.faqQ}>{item.q}</dt>
                      <dd className={css.faqA}>{item.a}</dd>
                    </div>
                  ))}
                </dl>

                <h2 className={css.h2}>Buying Guides & Market Insight</h2>
                <p>
                  Read independent Porsche buyer's guides, market reports, and
                  ownership advice on the{' '}
                  <a href="/blog" className={css.seeAlsoLink}>Rennzone blog</a>.
                  Coverage includes 993, 964, 996, 997, GT3, air-cooled,
                  race cars, and collector memorabilia.
                </p>

                <div className={css.seeAlso}>
                  <span className={css.seeAlsoLabel}>See also:</span>
                  <a href="/vs/rennlist" className={css.seeAlsoLink}>Rennlist Alternative</a>
                  <span className={css.seeAlsoSep}>|</span>
                  <a href="/cars/race-cars" className={css.seeAlsoLink}>Race Cars</a>
                  <span className={css.seeAlsoSep}>|</span>
                  <a href="/parts" className={css.seeAlsoLink}>Porsche Parts</a>
                  <span className={css.seeAlsoSep}>|</span>
                  <a href="/blog" className={css.seeAlsoLink}>Blog</a>
                </div>
              </div>

              {/* Right — sidebar */}
              <div className={css.sidebar}>
                <div className={css.sidebarCard}>
                  <p className={css.sidebarHeading}>Get Started</p>
                  <a href="/s" className={css.sidebarCtaPrimary}>Browse All Listings</a>
                  <a href="/l/new" className={css.sidebarCtaSecondary}>List Your Porsche</a>
                  <hr className={css.sidebarDivider} />
                  <p className={css.sidebarHeading}>Categories</p>
                  <ul className={css.sidebarLinkList}>
                    <li><a href="/s" className={css.sidebarLink}>All Cars →</a></li>
                    <li><a href="/cars/race-cars" className={css.sidebarLink}>Race Cars →</a></li>
                    <li><a href="/cars/air-cooled" className={css.sidebarLink}>Air-Cooled →</a></li>
                    <li><a href="/parts" className={css.sidebarLink}>Parts →</a></li>
                    <li><a href="/memorabilia" className={css.sidebarLink}>Memorabilia & Books →</a></li>
                  </ul>
                  <hr className={css.sidebarDivider} />
                  <p className={css.sidebarHeading}>Popular Models</p>
                  <ul className={css.sidebarLinkList}>
                    <li><a href="/cars/porsche-993" className={css.sidebarLink}>Porsche 993 →</a></li>
                    <li><a href="/cars/porsche-911-gt3" className={css.sidebarLink}>Porsche 911 GT3 →</a></li>
                    <li><a href="/cars/porsche-964" className={css.sidebarLink}>Porsche 964 →</a></li>
                    <li><a href="/cars/porsche-997" className={css.sidebarLink}>Porsche 997 →</a></li>
                    <li><a href="/cars/porsche-cayman" className={css.sidebarLink}>Porsche Cayman →</a></li>
                  </ul>
                  <hr className={css.sidebarDivider} />
                  <p className={css.sidebarHeading}>Read the Blog</p>
                  <a href="/blog" className={css.sidebarCtaSecondary}>Porsche Buyer's Guides →</a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default ClassifiedsPage;
